import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { CHAINS, getExplorerAddressUrl, getExplorerTxUrl, ROBINHOOD_TOKENS, getErrorMessage } from '../../services/api';
import { Icons } from './DashboardLayout';
import ActivityFeed from './ActivityFeed';
import { useSafeTransactions } from '../../services/websocket';

// Autonomy level config
const AUTONOMY_LEVELS = {
  1: { label: 'Manual', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', desc: 'swap, wrap, buy_tokens only' },
  2: { label: 'Guarded', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20', desc: '+ transfer, bridge, add_liquidity, lending' },
  3: { label: 'Autonomous', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', desc: '+ burn, lock, arbitrage, multi_chain' },
  4: { label: 'Unrestricted', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', desc: '+ flash_loans, no limits' },
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'wallets', label: 'Wallets' },
  { id: 'plugins', label: 'Plugins' },
  { id: 'security', label: 'Security' },
  { id: 'activity', label: 'Activity' },
  { id: 'api-keys', label: 'API Keys' },
];

export default function AgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { agents, token, refreshAgents } = useAuth();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [safeInfo, setSafeInfo] = useState(null);
  const [safeTxs, setSafeTxs] = useState([]);
  const [safePendingCount, setSafePendingCount] = useState(0);

  useEffect(() => {
    loadAgentData();
  }, [agentId, token]);

  const loadAgentData = async () => {
    setLoading(true);
    try {
      // First try to find in context (works for DEV_MODE)
      const contextAgent = agents.find(a => a.id === agentId);
      if (contextAgent) {
        setAgent(contextAgent);
        setLoading(false); // Set loading false immediately if found in context
      }
      
      // Then fetch fresh data from API (will fail in DEV_MODE but that's ok)
      try {
        const res = await api.getAgent(token, agentId);
        if (res.success && res.data) {
          setAgent(res.data.agent || res.data);
        }
      } catch (e) {
        console.log('API fetch failed, using context agent');
      }

      // Load Safe info
      try {
        const safeRes = await api.getSafeInfo(token, agentId);
        if (safeRes.success && safeRes.data) {
          setSafeInfo(safeRes.data);
        }
      } catch (e) {
        console.log('No Safe info available');
      }

      // Load pending Safe transactions
      try {
        const txRes = await api.getSafeTransactions(token, agentId, 'PROPOSED');
        if (txRes.success && txRes.data?.transactions) {
          setSafeTxs(txRes.data.transactions);
          setSafePendingCount(txRes.data.transactions.length);
        }
      } catch (e) {
        console.log('No Safe transactions');
      }
    } catch (err) {
      console.error('Failed to load agent:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div 
          className="w-10 h-10 border-2 border-white/10 border-t-white/60 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
          <Icons.Agents size={32} className="text-white/30" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Agent Not Found</h3>
        <p className="text-white/50 text-center max-w-md mb-6">
          The agent you're looking for doesn't exist or you don't have access to it.
        </p>
        <Link to="/dashboard" className="text-blue-400 hover:underline">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  const autonomy = AUTONOMY_LEVELS[agent.autonomyLevel] || AUTONOMY_LEVELS[2];
  const wallets = agent.wallets || [];
  const primaryWallet = wallets.find(w => w.isDefault) || wallets[0];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link to="/dashboard" className="text-white/40 hover:text-white/60">Agents</Link>
        <span className="text-white/20">/</span>
        <span className="text-white">{agent.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 flex items-center justify-center">
            <Icons.Agents size={28} className="text-white/60" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{agent.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <code className="text-xs text-white/40 font-mono">{agent.id}</code>
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${autonomy.color} ${autonomy.bg}`}>
                L{agent.autonomyLevel} {autonomy.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => navigate('/dashboard/plugins')}
            className="px-4 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white/70 text-sm hover:bg-white/[0.08] transition-colors"
          >
            Configure Plugins
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/[0.06]">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium transition-colors relative whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id 
                  ? 'text-white' 
                  : 'text-white/50 hover:text-white/70'
              }`}
              data-testid={`agent-tab-${tab.id}`}
            >
              {tab.label}
              {tab.id === 'security' && safePendingCount > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-[10px] font-bold">
                  {safePendingCount}
                </span>
              )}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeAgentTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <OverviewTab key="overview" agent={agent} autonomy={autonomy} wallets={wallets} token={token} onRefresh={loadAgentData} />
        )}
        {activeTab === 'wallets' && (
          <WalletsTab key="wallets" agent={agent} wallets={wallets} token={token} onRefresh={loadAgentData} />
        )}
        {activeTab === 'plugins' && (
          <PluginsTab key="plugins" agent={agent} token={token} />
        )}
        {activeTab === 'security' && (
          <SecurityTab 
            key="security" 
            agent={agent} 
            wallets={wallets}
            safeInfo={safeInfo}
            safeTxs={safeTxs}
            token={token} 
            onRefresh={loadAgentData} 
          />
        )}
        {activeTab === 'activity' && (
          <ActivityTab key="activity" agent={agent} token={token} />
        )}
        {activeTab === 'api-keys' && (
          <ApiKeysTab key="api-keys" agent={agent} token={token} />
        )}
      </AnimatePresence>
    </div>
  );
}

// Overview Tab
function OverviewTab({ agent, autonomy, wallets, token, onRefresh }) {
  const primaryWallet = wallets.find(w => w.isDefault) || wallets[0];
  const [editingLimits, setEditingLimits] = useState(false);
  const [limits, setLimits] = useState({
    maxPerTx: agent.spendingLimits?.maxPerTx ? (parseFloat(agent.spendingLimits.maxPerTx) / 1e18).toString() : '0.1',
    dailyLimit: agent.spendingLimits?.dailyLimit ? (parseFloat(agent.spendingLimits.dailyLimit) / 1e18).toString() : '1',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSaveLimits = async () => {
    setSaving(true);
    setError('');
    try {
      // Convert ETH values to wei (as strings)
      const maxPerTxWei = (parseFloat(limits.maxPerTx) * 1e18).toString();
      const dailyLimitWei = (parseFloat(limits.dailyLimit) * 1e18).toString();
      
      const res = await api.updateAgent(token, agent.id, {
        spendingLimits: {
          maxPerTx: maxPerTxWei,
          dailyLimit: dailyLimitWei,
        }
      });
      
      if (res.success) {
        setEditingLimits(false);
        onRefresh();
      } else {
        setError(res.error?.message || 'Failed to update limits');
      }
    } catch (err) {
      setError('Failed to update spending limits');
    }
    setSaving(false);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Status" value={agent.status || 'Active'} color="green" />
        <StatCard label="Autonomy" value={`L${agent.autonomyLevel} ${autonomy.label}`} color={autonomy.color.includes('blue') ? 'blue' : autonomy.color.includes('green') ? 'green' : autonomy.color.includes('yellow') ? 'yellow' : 'red'} />
        <StatCard label="Wallets" value={wallets.length} />
        <StatCard label="Plugins" value={agent.pluginConfigs?.filter(p => p.enabled).length || 0} subtitle="enabled" />
      </div>

      {/* Primary Wallet */}
      {primaryWallet && (
        <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <h3 className="text-white font-medium mb-4">Primary Wallet</h3>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-black/50 border border-white/[0.08] flex items-center justify-center">
              {CHAINS[primaryWallet.chain]?.logo ? (
                <img src={CHAINS[primaryWallet.chain].logo} alt="" className="w-6 h-6" />
              ) : (
                <Icons.Wallets size={20} className="text-white/40" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{CHAINS[primaryWallet.chain]?.name || primaryWallet.chain}</span>
                <span className="px-2 py-0.5 rounded text-[10px] font-medium text-white/50 bg-white/[0.05]">
                  {primaryWallet.type === 'GNOSIS_SAFE' ? 'Gnosis Safe' : 'Smart Account'}
                </span>
                {primaryWallet.isDefault && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium text-green-400 bg-green-500/10">
                    Active
                  </span>
                )}
              </div>
              <a 
                href={getExplorerAddressUrl(primaryWallet.chain, primaryWallet.address)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white/40 font-mono hover:text-white/60"
              >
                {primaryWallet.address}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Spending Limits - Editable */}
      <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium">Spending Limits</h3>
          {!editingLimits && (
            <button
              onClick={() => setEditingLimits(true)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/60 bg-white/[0.05] hover:bg-white/[0.08] transition-colors"
              data-testid="edit-limits-btn"
            >
              Edit Limits
            </button>
          )}
        </div>
        
        {editingLimits ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/40 text-xs uppercase tracking-wider mb-2">Max per Transaction (ETH)</label>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={limits.maxPerTx}
                  onChange={(e) => setLimits(prev => ({ ...prev, maxPerTx: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none focus:border-white/20 transition-colors"
                  data-testid="max-per-tx-input"
                />
              </div>
              <div>
                <label className="block text-white/40 text-xs uppercase tracking-wider mb-2">Daily Limit (ETH)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={limits.dailyLimit}
                  onChange={(e) => setLimits(prev => ({ ...prev, dailyLimit: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none focus:border-white/20 transition-colors"
                  data-testid="daily-limit-input"
                />
              </div>
            </div>
            
            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
            
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setEditingLimits(false);
                  setError('');
                  setLimits({
                    maxPerTx: agent.spendingLimits?.maxPerTx ? (parseFloat(agent.spendingLimits.maxPerTx) / 1e18).toString() : '0.1',
                    dailyLimit: agent.spendingLimits?.dailyLimit ? (parseFloat(agent.spendingLimits.dailyLimit) / 1e18).toString() : '1',
                  });
                }}
                className="px-4 py-2 rounded-xl text-white/60 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <motion.button
                onClick={handleSaveLimits}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="save-limits-btn"
              >
                {saving ? 'Saving...' : 'Save Limits'}
              </motion.button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Max per Transaction</p>
              <p className="text-white font-semibold">
                {agent.spendingLimits?.maxPerTx 
                  ? `${(parseFloat(agent.spendingLimits.maxPerTx) / 1e18).toFixed(4)} ETH`
                  : '0.1 ETH (default)'}
              </p>
            </div>
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Daily Limit</p>
              <p className="text-white font-semibold">
                {agent.spendingLimits?.dailyLimit 
                  ? `${(parseFloat(agent.spendingLimits.dailyLimit) / 1e18).toFixed(4)} ETH`
                  : '1 ETH (default)'}
              </p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StatCard({ label, value, subtitle, color }) {
  const colorClasses = {
    green: 'text-green-400',
    blue: 'text-blue-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400',
  };
  
  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
      <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-bold ${color ? colorClasses[color] : 'text-white'}`}>
        {value}
        {subtitle && <span className="text-white/40 text-sm font-normal ml-1">{subtitle}</span>}
      </p>
    </div>
  );
}

// Wallets Tab
function WalletsTab({ agent, wallets, token, onRefresh }) {
  const [addingChain, setAddingChain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawWallet, setWithdrawWallet] = useState(null);

  const availableChains = ['base', 'ethereum', 'arbitrum', 'optimism', 'polygon'].filter(
    chain => !wallets.some(w => w.chain === chain)
  );

  const handleAddWallet = async (chain) => {
    setLoading(true);
    try {
      await api.addWallet(token, agent.id, chain);
      onRefresh();
    } catch (err) {
      console.error('Failed to add wallet:', err);
    }
    setLoading(false);
    setAddingChain(null);
  };

  const openWithdrawModal = (wallet) => {
    setWithdrawWallet(wallet);
    setShowWithdrawModal(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* Wallets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {wallets.map((wallet) => (
          <WalletCard 
            key={wallet.address} 
            wallet={wallet} 
            onWithdraw={() => openWithdrawModal(wallet)}
          />
        ))}
      </div>

      {/* Add Chain */}
      {availableChains.length > 0 && (
        <div className="p-4 rounded-xl border border-dashed border-white/[0.08] bg-white/[0.01]">
          <p className="text-white/50 text-sm mb-3">Add wallet on another chain</p>
          <div className="flex flex-wrap gap-2">
            {availableChains.map((chain) => (
              <button
                key={chain}
                onClick={() => handleAddWallet(chain)}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white/70 text-sm hover:bg-white/[0.06] transition-colors disabled:opacity-50"
              >
                {CHAINS[chain]?.logo && (
                  <img src={CHAINS[chain].logo} alt="" className="w-4 h-4" />
                )}
                Add {CHAINS[chain]?.name || chain}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Withdraw Modal (v8.1) */}
      <AnimatePresence>
        {showWithdrawModal && withdrawWallet && (
          <WithdrawModal
            agentId={agent.id}
            wallet={withdrawWallet}
            token={token}
            onClose={() => {
              setShowWithdrawModal(false);
              setWithdrawWallet(null);
            }}
            onSuccess={() => {
              setShowWithdrawModal(false);
              setWithdrawWallet(null);
              onRefresh();
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function WalletCard({ wallet, onWithdraw }) {
  const chain = CHAINS[wallet.chain] || {};
  const [copied, setCopied] = useState(false);
  const isSafe = wallet.type === 'GNOSIS_SAFE';

  const handleCopy = () => {
    navigator.clipboard.writeText(wallet.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-black/50 border border-white/[0.08] flex items-center justify-center">
            {chain.logo ? <img src={chain.logo} alt="" className="w-5 h-5" /> : <Icons.Wallets size={18} className="text-white/40" />}
          </div>
          <div>
            <p className="text-white font-medium">{chain.name || wallet.chain}</p>
            <p className="text-white/40 text-xs">{isSafe ? 'Gnosis Safe' : 'Smart Account'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {wallet.isDefault && (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium text-green-400 bg-green-500/10">Active</span>
          )}
          {/* Withdraw Button (v8.1) */}
          <button
            onClick={onWithdraw}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 bg-white/[0.05] hover:bg-white/[0.08] transition-colors"
            data-testid="withdraw-btn"
            title={isSafe ? 'Withdraw requires Safe propose flow' : 'Withdraw funds'}
          >
            Withdraw
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 p-2 rounded-lg bg-black/30">
        <code className="flex-1 text-xs text-white/60 font-mono truncate">{wallet.address}</code>
        <button onClick={handleCopy} className="p-1 text-white/30 hover:text-white/60">
          {copied ? <span className="text-green-400 text-xs">✓</span> : <Icons.Copy size={14} />}
        </button>
        <a href={getExplorerAddressUrl(wallet.chain, wallet.address)} target="_blank" rel="noopener noreferrer" className="p-1 text-white/30 hover:text-white/60">
          <Icons.External size={14} />
        </a>
      </div>
    </div>
  );
}

// Plugins Tab
function PluginsTab({ agent, token }) {
  const plugins = agent.pluginConfigs || [];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10">
        <p className="text-blue-400/80 text-sm">
          Plugins control what your agent can do. Go to the <Link to="/dashboard/plugins" className="underline hover:text-blue-300">Plugins page</Link> to enable or configure them.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {plugins.map((plugin) => (
          <div 
            key={plugin.plugin}
            className={`p-4 rounded-xl border ${plugin.enabled ? 'bg-green-500/5 border-green-500/20' : 'bg-white/[0.01] border-white/[0.06]'}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-white font-medium capitalize">{plugin.plugin.replace('_', ' ')}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${plugin.enabled ? 'text-green-400 bg-green-500/10' : 'text-white/40 bg-white/[0.05]'}`}>
                {plugin.enabled ? 'ON' : 'OFF'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// Security Tab (Gnosis Safe)
function SecurityTab({ agent, wallets, safeInfo, safeTxs, token, onRefresh }) {
  const [deploying, setDeploying] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [switching, setSwitching] = useState(false);

  const safes = safeInfo?.safes || [];
  const hasSafe = safes.length > 0;
  const primaryWallet = wallets.find(w => w.isDefault) || wallets[0];
  const isSafeActive = primaryWallet?.type === 'GNOSIS_SAFE';
  const smartAccountWallet = wallets.find(w => w.type === 'SMART_ACCOUNT');
  const safeWallet = wallets.find(w => w.type === 'GNOSIS_SAFE');

  const handleSwitchWallet = async (walletId) => {
    setSwitching(true);
    try {
      await api.switchPrimaryWallet(token, agent.id, walletId);
      onRefresh();
    } catch (err) {
      console.error('Failed to switch wallet:', err);
    }
    setSwitching(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6"
    >
      {/* Wallet Type Switcher */}
      <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <h3 className="text-white font-medium mb-4">Active Wallet Type</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* Smart Account Option */}
          <button
            onClick={() => smartAccountWallet && handleSwitchWallet(smartAccountWallet.id)}
            disabled={switching || !smartAccountWallet || (!isSafeActive && primaryWallet?.type === 'SMART_ACCOUNT')}
            className={`p-4 rounded-xl border text-left transition-all ${
              !isSafeActive 
                ? 'bg-green-500/10 border-green-500/30' 
                : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'
            } ${switching ? 'opacity-50' : ''}`}
            data-testid="switch-to-smart-account"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">Smart Account</span>
              {!isSafeActive && <span className="w-2 h-2 rounded-full bg-green-400" />}
            </div>
            <p className="text-white/50 text-sm">Fast, single-signer, gas-sponsored</p>
          </button>

          {/* Gnosis Safe Option */}
          <button
            onClick={() => safeWallet && handleSwitchWallet(safeWallet.id)}
            disabled={switching || !hasSafe || (isSafeActive)}
            className={`p-4 rounded-xl border text-left transition-all ${
              isSafeActive 
                ? 'bg-green-500/10 border-green-500/30' 
                : hasSafe 
                  ? 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'
                  : 'bg-white/[0.01] border-white/[0.04] opacity-60'
            } ${switching ? 'opacity-50' : ''}`}
            data-testid="switch-to-gnosis-safe"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">Gnosis Safe</span>
              {isSafeActive && <span className="w-2 h-2 rounded-full bg-green-400" />}
            </div>
            <p className="text-white/50 text-sm">
              {hasSafe ? `Multi-sig (${safes[0]?.threshold}-of-${safes[0]?.signers?.length})` : 'Deploy a Safe first'}
            </p>
          </button>
        </div>

        {isSafeActive && (
          <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
            <p className="text-yellow-400/80 text-sm">
              <strong>Gnosis Safe Active:</strong> All plugin executions will require multi-sig approval.
            </p>
          </div>
        )}
      </div>

      {/* Deploy Safe Section */}
      {!hasSafe && (
        <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-medium">Deploy Gnosis Safe</h3>
              <p className="text-white/50 text-sm mt-1">Add multi-signature security to your agent</p>
            </div>
            <motion.button
              onClick={() => setShowDeployModal(true)}
              className="px-4 py-2 rounded-xl bg-white text-black font-medium text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="deploy-safe-btn"
            >
              Deploy Safe
            </motion.button>
          </div>
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-blue-400/80 text-sm">
              A Gnosis Safe is an on-chain multi-signature wallet. Once deployed, you can switch your agent's primary wallet to use the Safe for added security.
            </p>
          </div>
        </div>
      )}

      {/* Safe Info */}
      {hasSafe && safes.map((safe) => (
        <div key={safe.address} className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Safe Configuration</h3>
            <span className="px-2 py-1 rounded-lg text-xs font-medium text-white/60 bg-white/[0.05]">
              {safe.chain}
            </span>
          </div>
          
          <div className="space-y-4">
            {/* Address */}
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Safe Address</p>
              <a 
                href={getExplorerAddressUrl(safe.chain, safe.address)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 font-mono text-sm hover:underline"
              >
                {safe.address}
              </a>
            </div>

            {/* Threshold */}
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Threshold</p>
              <p className="text-white font-semibold">{safe.threshold} of {safe.signers?.length} signatures required</p>
            </div>

            {/* Signers */}
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Signers</p>
              <div className="space-y-2">
                {safe.signers?.map((signer, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-black/30">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] text-white/60">
                        {i + 1}
                      </div>
                      <code className="text-xs text-white/60 font-mono">{signer.address?.slice(0, 10)}...{signer.address?.slice(-8)}</code>
                    </div>
                    {signer.label && <span className="text-white/40 text-xs">{signer.label}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Pending Transactions */}
      {safeTxs.length > 0 && (
        <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Pending Transactions</h3>
            <span className="px-2 py-1 rounded-lg text-xs font-bold text-yellow-400 bg-yellow-500/10">
              {safeTxs.length} pending
            </span>
          </div>
          
          <div className="space-y-3">
            {safeTxs.map((tx) => (
              <SafeTxCard key={tx.id} tx={tx} token={token} onAction={onRefresh} />
            ))}
          </div>
        </div>
      )}

      {/* Danger Zone - Delete Agent (v8.1) */}
      <DangerZone agent={agent} wallets={wallets} token={token} />

      {/* Deploy Modal */}
      <AnimatePresence>
        {showDeployModal && (
          <DeploySafeModal
            agentId={agent.id}
            token={token}
            onClose={() => setShowDeployModal(false)}
            onSuccess={() => {
              setShowDeployModal(false);
              onRefresh();
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function SafeTxCard({ tx, token, onAction }) {
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  
  const confirmations = tx.confirmations?.length || 0;
  const threshold = tx.threshold || 2;
  const progress = (confirmations / threshold) * 100;
  const canExecute = confirmations >= threshold;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      // In real app, would need to sign with wallet
      await api.confirmSafeTx(token, tx.id, '', '');
      onAction();
    } catch (err) {
      console.error('Failed to confirm:', err);
    }
    setConfirming(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await api.rejectSafeTx(token, tx.id, '', 'Rejected by user');
      onAction();
    } catch (err) {
      console.error('Failed to reject:', err);
    }
    setRejecting(false);
  };

  const handleExecute = async () => {
    try {
      await api.executeSafeTx(token, tx.id);
      onAction();
    } catch (err) {
      console.error('Failed to execute:', err);
    }
  };

  return (
    <div className="p-4 rounded-xl bg-black/30 border border-white/[0.06]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium capitalize">{tx.plugin}</span>
          <span className="px-2 py-0.5 rounded text-[10px] font-medium text-yellow-400 bg-yellow-500/10">
            {tx.status}
          </span>
        </div>
        <span className="text-white/40 text-xs">{tx.value ? `${parseFloat(tx.value) / 1e18} ETH` : ''}</span>
      </div>
      
      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs text-white/40 mb-1">
          <span>Confirmations</span>
          <span>{confirmations}/{threshold}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div 
            className="h-full rounded-full bg-green-400 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {canExecute ? (
          <button
            onClick={handleExecute}
            className="flex-1 px-3 py-2 rounded-lg bg-green-500/20 text-green-400 text-sm font-medium hover:bg-green-500/30 transition-colors"
          >
            Execute
          </button>
        ) : (
          <>
            <button
              onClick={handleConfirm}
              disabled={confirming}
              className="flex-1 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/30 transition-colors disabled:opacity-50"
            >
              {confirming ? 'Confirming...' : 'Approve'}
            </button>
            <button
              onClick={handleReject}
              disabled={rejecting}
              className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              {rejecting ? '...' : 'Reject'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function DeploySafeModal({ agentId, token, onClose, onSuccess }) {
  const [threshold, setThreshold] = useState(2);
  const [signers, setSigners] = useState([
    { address: '', label: 'Me' },
    { address: '', label: '' }
  ]);
  const [chain, setChain] = useState('base');
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState('');

  const addSigner = () => {
    if (signers.length < 5) {
      setSigners([...signers, { address: '', label: '' }]);
    }
  };

  const removeSigner = (index) => {
    if (signers.length > 2) {
      setSigners(signers.filter((_, i) => i !== index));
      if (threshold > signers.length - 1) {
        setThreshold(signers.length - 1);
      }
    }
  };

  const updateSigner = (index, field, value) => {
    const updated = [...signers];
    updated[index][field] = value;
    setSigners(updated);
  };

  const handleDeploy = async () => {
    // Validate
    const validSigners = signers.filter(s => s.address && s.address.startsWith('0x'));
    if (validSigners.length < 2) {
      setError('At least 2 valid signer addresses required');
      return;
    }
    if (threshold < 1 || threshold > validSigners.length) {
      setError(`Threshold must be between 1 and ${validSigners.length}`);
      return;
    }

    setDeploying(true);
    setError('');
    
    try {
      const res = await api.deploySafe(token, agentId, chain, threshold, validSigners);
      if (res.success) {
        onSuccess();
      } else {
        setError(res.error?.message || 'Failed to deploy Safe');
      }
    } catch (err) {
      setError('Failed to deploy Safe');
    }
    setDeploying(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Deploy Gnosis Safe</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
          {/* Chain */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Chain</label>
            <select
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
            >
              <option value="base">Robinhood Network</option>
              <option value="ethereum">Ethereum</option>
              <option value="arbitrum">Arbitrum</option>
              <option value="optimism">Optimism</option>
              <option value="polygon">Polygon</option>
            </select>
          </div>

          {/* Threshold */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Threshold (signatures required)
            </label>
            <select
              value={threshold}
              onChange={(e) => setThreshold(parseInt(e.target.value))}
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
            >
              {[...Array(signers.length)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1} of {signers.length}</option>
              ))}
            </select>
          </div>

          {/* Signers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white/70">Signers</label>
              <button
                onClick={addSigner}
                disabled={signers.length >= 5}
                className="text-xs text-blue-400 hover:text-blue-300 disabled:text-white/30"
              >
                + Add Signer
              </button>
            </div>
            <div className="space-y-3">
              {signers.map((signer, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={signer.address}
                    onChange={(e) => updateSigner(index, 'address', e.target.value)}
                    placeholder="0x..."
                    className="flex-1 px-3 py-2 rounded-lg bg-black border border-white/10 text-white font-mono text-sm outline-none"
                  />
                  <input
                    type="text"
                    value={signer.label}
                    onChange={(e) => updateSigner(index, 'label', e.target.value)}
                    placeholder="Label"
                    className="w-24 px-3 py-2 rounded-lg bg-black border border-white/10 text-white text-sm outline-none"
                  />
                  {signers.length > 2 && (
                    <button
                      onClick={() => removeSigner(index)}
                      className="p-2 text-white/30 hover:text-red-400"
                    >
                      <Icons.Close size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-blue-400/80 text-sm">
              Your agent's Turnkey EOA will be automatically added as a signer. The Safe will be deployed on-chain.
            </p>
          </div>

          {/* Error */}
          {error && <p className="text-red-400 text-sm">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-white/60 hover:text-white">
            Cancel
          </button>
          <motion.button
            onClick={handleDeploy}
            disabled={deploying}
            className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {deploying ? 'Deploying...' : 'Deploy Safe'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Activity Tab - Uses real-time ActivityFeed
function ActivityTab({ agent, token }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <ActivityFeed agentId={agent.id} limit={20} showHeader={true} />
    </motion.div>
  );
}

// API Keys Tab
function ApiKeysTab({ agent, token }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newApiKey, setNewApiKey] = useState(null);

  const handleCreateKey = async () => {
    if (!newKeyLabel.trim()) return;
    setLoading(true);
    try {
      const res = await api.regenerateApiKey(token, agent.id);
      if (res.success && res.data?.apiKey) {
        setNewApiKey(res.data.apiKey);
      }
    } catch (err) {
      console.error('Failed to create key:', err);
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
        <p className="text-yellow-400/80 text-sm">
          <strong>Important:</strong> API keys are shown only once when created. Make sure to save them securely.
        </p>
      </div>

      {newApiKey && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
          <p className="text-white/70 text-sm mb-2">New API Key (save this now):</p>
          <code className="block p-3 rounded-lg bg-black text-green-400 font-mono text-sm break-all">{newApiKey}</code>
        </div>
      )}

      <div className="flex items-center gap-3">
        <input
          type="text"
          value={newKeyLabel}
          onChange={(e) => setNewKeyLabel(e.target.value)}
          placeholder="Key label (e.g., production)"
          className="flex-1 px-4 py-2.5 rounded-xl bg-black border border-white/10 text-white placeholder:text-white/30 outline-none"
        />
        <motion.button
          onClick={handleCreateKey}
          disabled={loading || !newKeyLabel.trim()}
          className="px-4 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
          whileTap={{ scale: 0.98 }}
        >
          {loading ? 'Creating...' : 'Create Key'}
        </motion.button>
      </div>

      <p className="text-white/40 text-sm">
        Max 5 active keys per agent. Your agent uses these keys to call the AIBL API autonomously.
      </p>
    </motion.div>
  );
}

// Withdraw Modal (v8.1)
function WithdrawModal({ agentId, wallet, token, onClose, onSuccess }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Select token, 2: Confirm
  const [selectedToken, setSelectedToken] = useState(null);
  const [customToken, setCustomToken] = useState('');
  const [amount, setAmount] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState(null);

  const isSafe = wallet.type === 'GNOSIS_SAFE';

  // Load balance when token selected
  useEffect(() => {
    if (selectedToken !== null) {
      loadBalance();
    }
  }, [selectedToken, customToken]);

  const loadBalance = async () => {
    setLoadingBalance(true);
    setBalance(null);
    setError('');
    try {
      const tokenAddress = selectedToken === 'custom' ? customToken : selectedToken;
      const res = await api.previewWithdrawal(token, agentId, wallet.chain, tokenAddress);
      if (res.success) {
        setBalance(res.data);
      } else {
        setError(res.error?.message || 'Failed to load balance');
      }
    } catch (err) {
      setError('Failed to load balance');
    }
    setLoadingBalance(false);
  };

  const handleMaxClick = () => {
    if (balance) {
      setAmount(balance.formatted);
    }
  };

  const handleWithdraw = async () => {
    if (!toAddress || !toAddress.startsWith('0x') || toAddress.length !== 42) {
      setError('Please enter a valid recipient address');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setWithdrawing(true);
    setError('');
    try {
      const tokenAddress = selectedToken === 'custom' ? customToken : selectedToken;
      // Convert to smallest unit
      const decimals = balance?.decimals || 18;
      const amountRaw = amount === balance?.formatted ? 'max' : (parseFloat(amount) * Math.pow(10, decimals)).toString();
      
      const res = await api.executeWithdrawal(token, agentId, {
        toAddress,
        chain: wallet.chain,
        token: tokenAddress,
        amount: amountRaw
      });

      if (res.success) {
        setTxHash(res.data.txHash);
        setStep(3); // Success step
      } else {
        const errorMsg = getErrorMessage(res.error);
        setError(errorMsg);
      }
    } catch (err) {
      setError('Withdrawal failed. Please try again.');
    }
    setWithdrawing(false);
  };

  // Get tokens based on chain (using ROBINHOOD_TOKENS for base)
  const tokens = wallet.chain === 'base' ? ROBINHOOD_TOKENS : [{ symbol: 'ETH', address: null, decimals: 18 }];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Withdraw Funds</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <Icons.Close size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Safe Warning */}
          {isSafe && (
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-yellow-400/80 text-sm">
                <strong>Gnosis Safe Detected:</strong> Direct withdrawals are not available for Safe wallets. 
                Use the <Link to="/dashboard/multisig" className="underline">Safe propose flow</Link> to initiate withdrawals.
              </p>
            </div>
          )}

          {!isSafe && step === 1 && (
            <>
              {/* Token Selection */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Select Token</label>
                <div className="grid grid-cols-3 gap-2">
                  {tokens.map((t) => (
                    <button
                      key={t.symbol}
                      onClick={() => setSelectedToken(t.address)}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        selectedToken === t.address
                          ? 'bg-white/10 border-white/20'
                          : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'
                      }`}
                    >
                      <span className="text-white font-medium text-sm">{t.symbol}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => setSelectedToken('custom')}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      selectedToken === 'custom'
                        ? 'bg-white/10 border-white/20'
                        : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'
                    }`}
                  >
                    <span className="text-white/60 text-sm">Custom</span>
                  </button>
                </div>
              </div>

              {selectedToken === 'custom' && (
                <input
                  type="text"
                  value={customToken}
                  onChange={(e) => setCustomToken(e.target.value)}
                  placeholder="Token contract address (0x...)"
                  className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none"
                />
              )}

              {/* Balance Display */}
              {loadingBalance && (
                <div className="p-4 rounded-xl bg-white/[0.02] text-center">
                  <p className="text-white/50 text-sm">Loading balance...</p>
                </div>
              )}
              {balance && !loadingBalance && (
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Available Balance</p>
                  <p className="text-white font-semibold text-lg">{balance.formatted} {balance.symbol}</p>
                </div>
              )}

              {/* Amount Input */}
              {selectedToken !== null && balance && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Amount</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.0"
                        className="w-full px-4 py-3 pr-20 rounded-xl bg-black border border-white/10 text-white outline-none"
                        data-testid="withdraw-amount-input"
                      />
                      <button
                        onClick={handleMaxClick}
                        className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 rounded-lg text-xs font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20"
                      >
                        MAX
                      </button>
                    </div>
                  </div>

                  {/* Recipient Address */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Recipient Address</label>
                    <input
                      type="text"
                      value={toAddress}
                      onChange={(e) => setToAddress(e.target.value)}
                      placeholder="0x..."
                      className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none"
                      data-testid="withdraw-recipient-input"
                    />
                  </div>

                  <motion.button
                    onClick={() => setStep(2)}
                    disabled={!amount || !toAddress}
                    className="w-full px-5 py-3 rounded-xl bg-white text-black font-medium disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Review Withdrawal
                  </motion.button>
                </>
              )}
            </>
          )}

          {!isSafe && step === 2 && (
            <>
              {/* Confirmation */}
              <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-4">
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Withdrawing</p>
                  <p className="text-white font-semibold text-xl">{amount} {balance?.symbol}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">To Address</p>
                  <p className="text-white font-mono text-sm break-all">{toAddress}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">From</p>
                  <p className="text-white text-sm">Agent Wallet ({CHAINS[wallet.chain]?.name || wallet.chain})</p>
                  <p className="text-white/50 font-mono text-xs">{wallet.address}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 px-4 py-3 rounded-xl text-white/60 bg-white/[0.05] hover:bg-white/[0.08]"
                >
                  Back
                </button>
                <motion.button
                  onClick={handleWithdraw}
                  disabled={withdrawing}
                  className="flex-1 px-5 py-3 rounded-xl bg-white text-black font-medium disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="confirm-withdraw-btn"
                >
                  {withdrawing ? 'Processing...' : 'Confirm Withdrawal'}
                </motion.button>
              </div>
            </>
          )}

          {/* Success Step */}
          {step === 3 && txHash && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-400">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">Withdrawal Successful!</h3>
              <p className="text-white/50 text-sm mb-4">Your funds are on the way</p>
              <a
                href={getExplorerTxUrl(wallet.chain, txHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.05] text-white/70 hover:bg-white/[0.08]"
              >
                View Transaction
                <Icons.External size={14} />
              </a>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        {step === 3 && (
          <div className="px-6 py-4 border-t border-white/[0.06]">
            <button
              onClick={onSuccess}
              className="w-full px-5 py-3 rounded-xl bg-white text-black font-medium"
            >
              Done
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// Danger Zone - Secure Delete (v8.1)
function DangerZone({ agent, wallets, token }) {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [step, setStep] = useState(1); // 1: Warning, 2: Balance check, 3: Confirm delete
  const [walletsWithFunds, setWalletsWithFunds] = useState([]);
  const [checkingBalances, setCheckingBalances] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const isConfirmed = confirmInput === 'CONFIRM-DELETE';

  const checkBalances = async () => {
    setCheckingBalances(true);
    try {
      // Add timeout for API calls (5 seconds)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 5000)
      );
      
      const balanceCheckPromise = Promise.all(
        wallets.map(async (wallet) => {
          const res = await api.previewWithdrawal(token, agent.id, wallet.chain);
          return { wallet, balance: res.data };
        })
      );
      
      const balanceChecks = await Promise.race([balanceCheckPromise, timeoutPromise]);
      const withFunds = balanceChecks.filter(
        (b) => b.balance && parseFloat(b.balance.formatted) > 0
      );
      setWalletsWithFunds(withFunds);
      
      if (withFunds.length > 0) {
        setStep(2);
      } else {
        setStep(3);
      }
    } catch (err) {
      // If balance check fails or times out, proceed to step 3
      console.log('Balance check failed, proceeding to confirm step');
      setStep(3);
    }
    setCheckingBalances(false);
  };

  const handleDelete = async () => {
    if (!isConfirmed) return;
    
    setDeleting(true);
    setError('');
    try {
      const res = await api.deleteAgent(token, agent.id, 'CONFIRM-DELETE');
      if (res.success) {
        navigate('/dashboard');
      } else {
        setError(res.error?.message || 'Failed to delete agent');
      }
    } catch (err) {
      setError('Failed to delete agent. Please try again.');
    }
    setDeleting(false);
  };

  return (
    <>
      <div className="p-5 rounded-2xl bg-red-500/5 border border-red-500/10">
        <h3 className="text-red-400 font-medium mb-2">Danger Zone</h3>
        <p className="text-white/50 text-sm mb-4">
          Permanently delete this agent and all associated wallets, API keys, and transaction history.
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="px-4 py-2 rounded-xl bg-red-500/10 text-red-400 font-medium text-sm hover:bg-red-500/20 transition-colors"
          data-testid="delete-agent-btn"
        >
          Delete Agent
        </button>
      </div>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-red-500/20 bg-red-500/5">
                <h2 className="text-lg font-semibold text-red-400">⚠️ Delete Agent "{agent.name}"?</h2>
              </div>

              <div className="p-6 space-y-5">
                {/* Step 1: Warning */}
                {step === 1 && (
                  <>
                    <div className="space-y-3 text-white/70 text-sm">
                      <p>This action is <strong className="text-red-400">PERMANENT and IRREVERSIBLE</strong>.</p>
                      <p>When you delete this agent:</p>
                      <ul className="list-disc list-inside space-y-1 text-white/50">
                        <li>All wallets associated with this agent will be removed</li>
                        <li>All API keys will be revoked immediately</li>
                        <li>All transaction history will be deleted</li>
                        <li>Any funds remaining in the agent's wallets will become inaccessible</li>
                      </ul>
                      <p className="text-yellow-400">
                        If your agent has funds, <strong>withdraw them FIRST</strong> using the Withdraw feature before deleting.
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowDeleteModal(false)}
                        className="flex-1 px-4 py-3 rounded-xl text-white/60 bg-white/[0.05]"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={checkBalances}
                        disabled={checkingBalances}
                        className="flex-1 px-4 py-3 rounded-xl bg-red-500/10 text-red-400 font-medium"
                      >
                        {checkingBalances ? 'Checking...' : 'I Understand, Continue →'}
                      </button>
                    </div>
                  </>
                )}

                {/* Step 2: Balance Warning */}
                {step === 2 && walletsWithFunds.length > 0 && (
                  <>
                    <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                      <h4 className="text-yellow-400 font-medium mb-2">⚠️ This agent has funds!</h4>
                      <div className="space-y-2">
                        {walletsWithFunds.map(({ wallet, balance }) => (
                          <div key={wallet.address} className="flex items-center justify-between text-sm">
                            <span className="text-white/60 font-mono">
                              {wallet.address.slice(0, 8)}...{wallet.address.slice(-6)} ({CHAINS[wallet.chain]?.name})
                            </span>
                            <span className="text-white font-medium">{balance.formatted} {balance.symbol}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-yellow-400/70 text-sm mt-3">
                        These funds will become inaccessible if you delete this agent.
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setStep(1);
                        }}
                        className="flex-1 px-4 py-3 rounded-xl bg-white/[0.05] text-white/70"
                      >
                        ← Go Back and Withdraw
                      </button>
                      <button
                        onClick={() => setStep(3)}
                        className="flex-1 px-4 py-3 rounded-xl bg-red-500/10 text-red-400"
                      >
                        Delete Anyway →
                      </button>
                    </div>
                  </>
                )}

                {/* Step 3: Confirm Delete */}
                {step === 3 && (
                  <>
                    <p className="text-white/70 text-sm">
                      Type <code className="px-2 py-0.5 rounded bg-white/10 text-red-400 font-mono">CONFIRM-DELETE</code> to permanently delete this agent:
                    </p>
                    <input
                      type="text"
                      value={confirmInput}
                      onChange={(e) => setConfirmInput(e.target.value)}
                      placeholder="Type CONFIRM-DELETE"
                      className="w-full px-4 py-3 rounded-xl bg-black border border-red-500/20 text-white font-mono outline-none focus:border-red-500/40"
                      data-testid="confirm-delete-input"
                    />

                    {error && (
                      <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setStep(1);
                          setConfirmInput('');
                        }}
                        className="flex-1 px-4 py-3 rounded-xl text-white/60 bg-white/[0.05]"
                      >
                        Cancel
                      </button>
                      <motion.button
                        onClick={handleDelete}
                        disabled={!isConfirmed || deleting}
                        className="flex-1 px-4 py-3 rounded-xl bg-red-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={isConfirmed ? { scale: 1.02 } : {}}
                        whileTap={isConfirmed ? { scale: 0.98 } : {}}
                        data-testid="confirm-delete-btn"
                      >
                        {deleting ? 'Deleting...' : 'Delete Agent'}
                      </motion.button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

